import { useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useSearchParam } from 'react-use'
import type { PivotLayout } from '@ztechapps/common.contract'
import type { OnChangeTemplateFunction } from './index'
import { useDeepCompareEffect, useTemplateDocument } from '@ztechapps/common.hook'
import { parsePivotLayout } from '../pivot/parsePivotLayout'

export type UsePivotDefaultTemplateResult = {
  defaultLayout: PivotLayout | undefined
  loading: boolean
}

export const usePivotDefaultTemplate = ({
  templateCategory,
  onChangeTemplate,
  onApplyFilterTemplate,
}: {
  templateCategory?: string
  onChangeTemplate?: OnChangeTemplateFunction
  onApplyFilterTemplate?: (filterTemplateId: string) => void
} = {}): UsePivotDefaultTemplateResult => {
  const pivotTemplateId = useSearchParam('templateId')

  //an explicit templateId is read as is; otherwise the user's own default is read first
  const { document: firstDocument, loading: firstLoading } = useTemplateDocument({
    filter: pivotTemplateId
      ? { _id: pivotTemplateId }
      : { category: templateCategory, isDefault: true, 'scope.key': 'user' },
  })

  //the tenant/app default is read only when the user has no default of their own
  const fallbackSkipped = !!pivotTemplateId || firstLoading || !!firstDocument
  const { document: fallbackDocument, loading: fallbackLoading } = useTemplateDocument({
    filter: { category: templateCategory, isDefault: true },
    options: { queryOptions: { skip: fallbackSkipped } },
  })

  const document = firstDocument ?? fallbackDocument
  const loading = firstLoading || (!firstDocument && fallbackLoading)

  // the empty layout when no template exists; undefined while the lookup is still running
  const defaultLayout = useMemo(
    () => (loading ? undefined : parsePivotLayout(document?.value)),
    [loading, document?.value]
  )

  useDeepCompareEffect(() => {
    if (!isEmpty(document?.value)) {
      //set also the pivot template here
      onChangeTemplate?.({ template: document })
      if (document.autoApplyFilterTemplate && document.filterTemplateId)
        onApplyFilterTemplate?.(document.filterTemplateId)
    }
  }, [document])

  return {
    defaultLayout,
    loading,
  }
}
